import React from "react";
import { Checkmark16Filled, Filter24Regular, Search24Regular } from "@fluentui/react-icons";
import { MailSearchInput } from "../MailSearchInput";
import "./mailMobil.css";

export interface MailMobilKopfProps {
    /** Kürzel im Kreis (Postfach). */
    kuerzel: string;
    /** Name/Adresse des aktiven Postfachs (kleine Zeile). */
    postfach: string;
    /** Aktiver Ordner (große Zeile). */
    ordner: string;
    /** Ungelesenes in einem ANDEREN lesbaren Postfach → Punkt am Kreis (wie Outlook). */
    punkt?: boolean;
    onMenue: () => void;
    suche: string;
    onSuche: (q: string) => void;
    sucheLaeuft?: boolean;
    filterAktiv: boolean;
    onFilter: () => void;
}

/**
 * Kopf der Mailliste auf dem Handy (Outlook iOS): links das Oval mit Postfach
 * und Ordner — antippen öffnet das Seitenmenü —, rechts die Pille mit Suche
 * und Filter. Solange gesucht wird, steht dort das Suchfeld mit „Abbrechen".
 */
export const MailMobilKopf: React.FC<MailMobilKopfProps> = ({
    kuerzel,
    postfach,
    ordner,
    punkt,
    onMenue,
    suche,
    onSuche,
    sucheLaeuft,
    filterAktiv,
    onFilter,
}) => {
    const [sucheOffen, setSucheOffen] = React.useState(false);
    const sucheRef = React.useRef<HTMLDivElement | null>(null);
    const zeigeSuche = sucheOffen || suche.trim() !== "";

    // Beim Öffnen gleich ins Feld — die Bildschirmtastatur kommt mit.
    React.useEffect(() => {
        if (!sucheOffen) return;
        sucheRef.current?.querySelector("input")?.focus();
    }, [sucheOffen]);

    if (zeigeSuche) {
        return (
            <div className="mm-kopf">
                <div className="mm-suche" ref={sucheRef}>
                    <MailSearchInput
                        value={suche}
                        onChange={onSuche}
                        size="large"
                        busy={sucheLaeuft}
                        placeholder="E-Mails durchsuchen …"
                        style={{ flex: 1, minWidth: 0 }}
                    />
                    <button
                        type="button"
                        className="mm-textknopf"
                        onClick={() => {
                            onSuche("");
                            setSucheOffen(false);
                        }}
                    >
                        Abbrechen
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="mm-kopf">
            <button
                type="button"
                className="mm-oval"
                onClick={onMenue}
                aria-label={`${ordner} in ${postfach} — Postfach oder Ordner wechseln`}
            >
                <span className="mm-kreis" aria-hidden>
                    {kuerzel}
                    {punkt && <span className="mm-punkt" />}
                </span>
                <span className="mm-oval-text">
                    <span className="mm-oval-ordner">{ordner}</span>
                    <span className="mm-oval-postfach">{postfach}</span>
                </span>
            </button>
            <div className="mm-pille">
                <button
                    type="button"
                    className="mm-pille-btn"
                    aria-label="Suchen"
                    title="Suchen"
                    onClick={() => setSucheOffen(true)}
                >
                    <Search24Regular />
                </button>
                <button
                    type="button"
                    className="mm-pille-btn"
                    aria-label={filterAktiv ? "Filter (aktiv)" : "Filter"}
                    title="Filter"
                    aria-pressed={filterAktiv}
                    onClick={onFilter}
                >
                    <Filter24Regular />
                    {filterAktiv && <span className="mm-punkt" aria-hidden />}
                </button>
            </div>
        </div>
    );
};

export interface MailAuswahlKopfProps {
    anzahl: number;
    alleAn: boolean;
    onAlle: () => void;
    onAbbrechen: () => void;
}

/** Kopf im Auswahlmodus (Outlook iOS): „3 ausgewählt" · Abbrechen · Alle auswählen. */
export const MailAuswahlKopf: React.FC<MailAuswahlKopfProps> = ({ anzahl, alleAn, onAlle, onAbbrechen }) => (
    <div className="mm-auswahl-kopf">
        <div className="mm-auswahl-zeile">
            <span className="mm-auswahl-titel" aria-live="polite">
                {anzahl} ausgewählt
            </span>
            <button type="button" className="mm-pillenknopf" onClick={onAbbrechen}>
                Abbrechen
            </button>
        </div>
        <button type="button" className="mm-alle" onClick={onAlle} aria-pressed={alleAn}>
            <span className="mm-check" data-an={alleAn} aria-hidden>
                {alleAn && <Checkmark16Filled />}
            </span>
            {alleAn ? "Keine auswählen" : "Alle auswählen"}
        </button>
    </div>
);

/** Der Auswahl-Kreis an der Zeile (statt des Bildes). */
export const AuswahlKreis: React.FC<{ an: boolean }> = ({ an }) => (
    <span className="mm-check" data-an={an} aria-hidden>
        {an && <Checkmark16Filled />}
    </span>
);
